import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import type { InsuranceEstimate } from '@/models/insuranceEstimate'
import { type LeadFunnelVisit, type SalesLead, useLeadEstimates } from '@/models/salesLead'

function money(value: number | null): string {
  return value === null ? '—' : `$${value.toLocaleString('en-US')}`
}

function EstimateRow({ estimate }: { estimate: InsuranceEstimate }) {
  const priced = estimate.perTruckLow !== null && estimate.perTruckHigh !== null

  return (
    <Card className="border-background shadow-none">
      <CardContent className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          {estimate.iconUrl && (
            <img src={estimate.iconUrl} alt={estimate.iconAlt} className="size-5 object-contain" />
          )}
          <span className="font-semibold">{estimate.vendorName}</span>
          <Badge variant={priced ? 'default' : 'secondary'}>{estimate.status}</Badge>
        </div>
        {priced ? (
          <div className="text-sm">
            <span className="font-semibold">
              {money(estimate.perTruckLow)}–{money(estimate.perTruckHigh)}
            </span>
            <span className="text-muted-foreground"> / truck / yr · {estimate.truckCount} trucks</span>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">{estimate.declineReason || 'Not eligible'}</span>
        )}
      </CardContent>
    </Card>
  )
}

function VisitRow({ visit }: { visit: LeadFunnelVisit }) {
  return (
    <li className="text-muted-foreground text-sm">
      DOT {visit.dotNumber} entered {new Date(visit.dateCreated).toLocaleString()}
      {visit.handedOffAt && ' · clicked through to the app'}
      {visit.adoptedOrgId && ' · signed in and started an application'}
    </li>
  )
}

/** Web-funnel estimates for this lead, plus the visits that produced them. */
export function EstimatesTab({ lead }: { lead: SalesLead }) {
  const { data, isLoading } = useLeadEstimates(lead.id)

  if (isLoading) return <SkeletonRows className="py-2" rows={3} rowClassName="h-14" />

  const estimates = data?.estimates ?? []
  const sessions = data?.sessions ?? []

  if (estimates.length === 0 && sessions.length === 0) {
    return (
      <p className="text-muted-foreground py-2 text-sm">
        No web estimates yet. These appear once this carrier looks itself up on the Cai website.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      {estimates.length > 0 && (
        <div className="flex flex-col gap-2">
          {estimates.map((estimate) => (
            <EstimateRow key={estimate.id} estimate={estimate} />
          ))}
        </div>
      )}
      {sessions.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="eyebrow self-start">Website visits</span>
          <ul className="flex flex-col gap-1">
            {sessions.map((visit) => (
              <VisitRow key={visit.id} visit={visit} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
